/**
 * Actores REALES de subida (F3-F) para inyectar en `mediaUploadMachine` (F2-B).
 * Traducen los pasos de la máquina a llamadas del plano de control (F3-E) y a
 * PUTs DIRECTOS navegador→R2 de cada parte (plano de datos). El ETag se lee de
 * la cabecera de la respuesta del PUT — depende de `CORS ExposeHeaders: ETag`
 * en el bucket (sin eso, `complete` multipart rompe).
 *
 * El PROGRESO se reporta por un callback inyectado (`onProgress`) que la UI
 * cablea a `actor.send({type:"PROGRESS", …})`, porque un actor `fromPromise` no
 * puede emitir eventos al padre por sí mismo.
 */

import { fromPromise } from "xstate";
import type {
  AbortUploadInput,
  CompletedPart,
  CompleteUploadInput,
  CreateUploadInput,
  CreateUploadResult,
  UploadPartsInput,
  VideoAssetMeta,
} from "@space/shared";
import type { MediaApi } from "./media-api";

export interface UploadActorContext {
  /** Curso/lección/sesión a los que se asocia el vídeo (creación). */
  courseId?: string | null;
  lessonId?: string | null;
  classSessionId?: string | null;
}

/** Sentinela de PAUSA: `uploadParts` lo lanza en el borde de parte cuando la UI
 * pide pausar → la máquina va a `failed`; la UI lo trata como "pausado" y RETRY
 * reanuda desde las partes ya subidas (sin abortar en el servidor). */
export const PAUSE_SENTINEL = "__paused__";

export interface UploadActorDeps {
  api: MediaApi;
  /** Contexto de asociación del asset (solo en creación nueva). */
  context?: UploadActorContext;
  /** Notifica progreso; la UI lo reenvía como evento PROGRESS a la máquina. */
  onProgress?: (uploadedCount: number, uploadedParts: CompletedPart[]) => void;
  /** Si devuelve true, `uploadParts` pausa en el próximo borde de parte. */
  shouldPause?: () => boolean;
}

/** Extrae el ETag (limpio) de la respuesta del PUT de una parte. */
function readEtag(res: Response, partNumber: number): string {
  const raw = res.headers.get("ETag") ?? res.headers.get("etag");
  if (!raw) {
    throw new Error(
      `parte ${partNumber}: sin cabecera ETag (revisa CORS ExposeHeaders: ETag en el bucket)`,
    );
  }
  return raw.trim();
}

function missingPartNumbers(totalParts: number, done: CompletedPart[]): number[] {
  const doneSet = new Set(done.map((p) => p.partNumber));
  const out: number[] = [];
  for (let n = 1; n <= totalParts; n++) {
    if (!doneSet.has(n)) {
      out.push(n);
    }
  }
  return out;
}

function toFile(blob: unknown): Blob {
  if (blob instanceof Blob) {
    return blob;
  }
  throw new Error("upload: falta el File/Blob real (file.blob)");
}

/**
 * Construye el set de actores para `mediaUploadMachine.provide({ actors })`.
 */
export function createUploadActors(deps: UploadActorDeps) {
  const { api, context, onProgress, shouldPause } = deps;

  const createUpload = fromPromise(
    async ({ input }: { input: CreateUploadInput }): Promise<CreateUploadResult> => {
      // Modo REANUDAR: list_parts para no re-subir lo ya subido.
      if (input.resume) {
        const status = await api.getUpload(input.resume.uploadId);
        const partSize = status.partSizeBytes;
        const totalParts = Math.max(1, Math.ceil(status.sizeBytes / partSize));
        return {
          assetId: status.assetId,
          uploadId: status.uploadId,
          storageKey: "",
          partSize,
          totalParts,
          uploadedParts: status.uploadedParts,
        };
      }
      // Creación nueva.
      const created = await api.createUpload({
        fileName: input.file.name,
        sizeBytes: input.file.size,
        contentType: input.file.type || "video/mp4",
        title: input.title,
        courseId: context?.courseId ?? null,
        lessonId: context?.lessonId ?? null,
        classSessionId: context?.classSessionId ?? null,
      });
      const partSize = created.partSizeBytes;
      const totalParts = Math.max(1, Math.ceil(input.file.size / partSize));
      return {
        assetId: created.assetId,
        uploadId: created.uploadId,
        storageKey: "",
        partSize,
        totalParts,
        uploadedParts: [],
      };
    },
  );

  const uploadParts = fromPromise(
    async ({
      input,
      signal,
    }: {
      input: UploadPartsInput;
      signal: AbortSignal;
    }): Promise<CompletedPart[]> => {
      const file = toFile(input.file.blob);
      const completed: CompletedPart[] = [...input.uploadedParts];
      const missing = missingPartNumbers(input.totalParts, completed);
      if (missing.length === 0) {
        return completed.toSorted((a, b) => a.partNumber - b.partNumber);
      }
      const urls = await api.partUrls(input.uploadId, missing);
      const urlByPart = new Map(urls.map((u) => [u.partNumber, u.url]));

      for (const partNumber of missing) {
        if (signal.aborted) {
          throw new DOMException("upload aborted", "AbortError");
        }
        if (shouldPause?.()) {
          throw new Error(PAUSE_SENTINEL);
        }
        const url = urlByPart.get(partNumber);
        if (!url) {
          throw new Error(`parte ${partNumber}: sin URL presignada`);
        }
        const start = (partNumber - 1) * input.partSize;
        const end = Math.min(start + input.partSize, file.size);
        const chunk = file.slice(start, end);
        const res = await fetch(url, { method: "PUT", body: chunk, signal });
        if (!res.ok) {
          throw new Error(`parte ${partNumber}: PUT HTTP ${res.status}`);
        }
        completed.push({ partNumber, etag: readEtag(res, partNumber) });
        onProgress?.(completed.length, [...completed]);
      }
      return completed.toSorted((a, b) => a.partNumber - b.partNumber);
    },
  );

  const completeUpload = fromPromise(
    async ({ input }: { input: CompleteUploadInput }): Promise<VideoAssetMeta> =>
      api.completeUpload(input.uploadId, input.parts),
  );

  const abortUpload = fromPromise(async ({ input }: { input: AbortUploadInput }): Promise<void> => {
    await api.abortUpload(input.uploadId);
  });

  return { createUpload, uploadParts, completeUpload, abortUpload };
}
